/**
 * Status lozenge — Phase 4b inline atomic chip (wiki feature plan §4b).
 *
 * Inline pill rendered as `<span data-block-type="status-lozenge">`.
 * Click opens `<StatusEditPopover>` (color picker + text input). On
 * commit, calls `updateAttributes({color, text})`.
 *
 * Schema shape — inline atomic conventions:
 *   - `group: 'inline'`           — flows inline with surrounding text.
 *   - `inline: true`              — same.
 *   - `atom: true`                — atomic: no cursor inside the node,
 *                                   no editable content. Edits happen
 *                                   via the popover, not in-place.
 *   - `selectable: true`          — can become a NodeSelection (used by
 *                                   the click-handler to open the popover
 *                                   AND by keyboard nav across atoms).
 *   - `draggable: false`          — relies on ProseMirror's native
 *                                   inline-atom drag; no custom handle.
 *   - `marks: ''`                 — no marks (bold / italic) apply to a
 *                                   status chip. The text rendering is
 *                                   per-chip, not per-mark.
 *
 * Attributes:
 *   - `color: StatusColor` — one of the 6-color enum. Default `'blue'`.
 *   - `text: string` — free-form chip label. Empty string falls back to
 *     the localized color name at render time (the NodeView resolves
 *     `wiki.status.colors.<color>`).
 *
 * Round-trip:
 *   - parseHTML matches `span[data-block-type="status-lozenge"]`.
 *   - renderHTML emits the canonical shape with `data-color` + `data-text`.
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

import { DEFAULT_STATUS_COLOR, isStatusColor } from './status-kinds'

export interface BuildStatusLozengeExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildStatusLozengeExtension({ view }: BuildStatusLozengeExtensionOptions) {
  return Node.create({
    name: 'statusLozenge',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    marks: '',

    addAttributes() {
      return {
        color: {
          default: DEFAULT_STATUS_COLOR,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-color')
            return isStatusColor(raw) ? raw : DEFAULT_STATUS_COLOR
          },
          renderHTML: (attrs: { color: string }) => ({ 'data-color': attrs.color }),
        },
        text: {
          default: '',
          parseHTML: (el: HTMLElement) => el.getAttribute('data-text') ?? '',
          renderHTML: (attrs: { text: string }) => ({ 'data-text': attrs.text }),
        },
      }
    },

    parseHTML() {
      return [{ tag: 'span[data-block-type="status-lozenge"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return ['span', { ...HTMLAttributes, 'data-block-type': 'status-lozenge' }]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },
  })
}
